import { useCallback, useState } from 'react';
import { useLang } from 'hooks/lang';

import { FullProject, outsourceInvoiceConfigByCode } from 'entities';

import { Column, Row, Collapse, IconButton, Table, Tag, Card } from 'components/Blocks';
import { ChevronRightIcon, ReceiptLongIcon } from 'components/Icons';

import { useToggleV2 } from 'hooks';

import { OutsourceInvoiceModal } from 'pages/Invoice/components';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { formatCurrency } from 'utils/text';
import { cn } from 'styles';

export function OutsourceInvoices() {
  const { t } = useLang();

  const { project, updateProject } = useProjectProfile();

  const [invoice, setInvoice] = useState<string | null>(null);

  const { outsourceInvoices } = project.invoice;

  const [showInvoices, vizHandlers] = useToggleV2(true);

  const onUpdate = useCallback(
    (newData: Omit<FullProject['invoice']['outsourceInvoices'][number], 'project'>) => {
      updateProject((old) => ({
        ...old,

        invoice: {
          ...old.invoice,

          outsourceInvoices: old.invoice.outsourceInvoices.map((existing) =>
            existing.id === newData.id ? { ...newData, project: old.id } : existing,
          ),
        },
      }));
    },
    [updateProject],
  );

  if (!outsourceInvoices.length) return null;

  return (
    <>
      <OutsourceInvoiceModal invoice={invoice} close={() => setInvoice(null)} onUpdate={onUpdate} />

      <Card flow="column">
        <Row gap="2">
          <IconButton className="text-primary" onClick={vizHandlers.toggle}>
            <ChevronRightIcon className={cn('transition-transform', showInvoices && 'rotate-90')} />
          </IconButton>

          <h6>{t('outsource-invoices')}</h6>
        </Row>

        <Collapse expanded={showInvoices} onlyRenderIfExpanded>
          <Column>
            <Table
              items={outsourceInvoices}
              uniqueKey="id"
              headerClassName="pb-4"
              columns={[
                {
                  label: t('provider'),
                  property: 'providerName',
                  filter: true,
                },
                {
                  label: t('service'),
                  property: 'serviceName',
                  filter: true,
                },

                {
                  label: t('provider-cost'),
                  render: ({ serviceConfigId }) => {
                    const service = project.services.find(({ id }) => id === serviceConfigId);

                    return formatCurrency(
                      service?.outsourceConfig?.providerCost.value as string,
                      service?.outsourceConfig?.providerCost.currency,
                    );
                  },
                },

                {
                  label: t('cost-to-client'),
                  render: ({ serviceConfigId }) => {
                    const service = project.services.find(({ id }) => id === serviceConfigId);

                    return formatCurrency(
                      service?.outsourceConfig?.clientCost.value as string,
                      service?.outsourceConfig?.clientCost.currency,
                    );
                  },
                },

                {
                  label: t('margin'),
                  render: ({ serviceConfigId }) => {
                    const service = project.services.find(({ id }) => id === serviceConfigId);

                    const percentage =
                      (Number(service?.outsourceConfig?.clientCost.value) -
                        Number(service?.outsourceConfig?.providerCost.value)) /
                      Number(service?.outsourceConfig?.providerCost.value);

                    return (
                      <span
                        className={percentage >= 0 ? 'text-success' : 'text-destructive'}
                      >{`${(percentage * 100).toFixed(2)} %`}</span>
                    );
                  },
                },

                {
                  label: t('invoice-value'),
                  className: 'text-center',

                  render: ({ value, currency }) => (value ? formatCurrency(value, currency) : '-'),
                },

                {
                  label: t('status') as string,
                  className: 'text-center',
                  filter: true,
                  getTextValue: ({ status }) => outsourceInvoiceConfigByCode[status].getLabel(t),

                  render: ({ status }) => (
                    <Tag
                      name={outsourceInvoiceConfigByCode[status].getLabel(t)}
                      color={outsourceInvoiceConfigByCode[status].color}
                      size="medium"
                    />
                  ),
                },

                {
                  className: 'w-14',

                  render: ({ id }) => (
                    <IconButton
                      onClick={() => setInvoice(id)}
                      tooltipContent={t('open')}
                      className="ml-auto flex"
                    >
                      <ReceiptLongIcon />
                    </IconButton>
                  ),
                },
              ]}
            />
          </Column>
        </Collapse>
      </Card>
    </>
  );
}
